import {
    FormEventHandler,
    ReactElement,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react';
import { useRequestEmailChangeAction } from '../actions';
import {
    UIAnimation,
    UIFormInput,
    UIFormInputRef,
    UIModal,
    UIModalFooter,
    UISection,
    UISectionContent,
    UISectionFooter,
    UISectionHeader,
    UIText
} from '@/ui';
import emailInviteSent from '@/common/assets/lotties/email-invite-sent.json';
import { validateEmail } from '@/common/util';
import { Trans } from 'react-i18next';
import { useMeTranslation } from '../hooks';

export function ChangeEmailSection(): ReactElement {
    const { t } = useMeTranslation();
    const formRef = useRef<ChangeEmailSectionFormRef>(null);

    const requestEmailChange = useRequestEmailChangeAction();

    const [showSuccessModal, setShowSuccessModal] = useState<{
        newEmail: string;
    } | null>(null);

    function onSubmit(data: { newEmail: string; password: string }) {
        const { newEmail, password } = data;
        requestEmailChange
            .requestEmailChange({ newEmail, password })
            .then(() => {
                formRef.current?.resetForm();
                setShowSuccessModal({ newEmail });
            })
            .catch(() => {});
    }

    function onSuccessClose() {
        setShowSuccessModal(null);
    }

    return (
        <>
            <ChangeEmailSectionForm
                ref={formRef}
                loading={requestEmailChange.loading}
                onSubmit={onSubmit}
            />
            <UIModal
                isOpen={showSuccessModal !== null}
                onClose={onSuccessClose}
            >
                {showSuccessModal && (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: 32,
                            gap: 16
                        }}
                    >
                        <UIAnimation
                            animationData={emailInviteSent}
                            loop={false}
                            style={{
                                height: 400
                            }}
                        />
                        <UIText variant="h1" textAlign="center">
                            {t('components.change_email_section.sent_title')}
                        </UIText>
                        <UIText textAlign="center">
                            <Trans
                                ns="me"
                                i18nKey="components.change_email_section.sent_description"
                                values={{ email: showSuccessModal.newEmail }}
                                components={{
                                    b: <UIText bold type="link" />,
                                    b1: <UIText bold />
                                }}
                            />
                        </UIText>
                    </div>
                )}
                <UIModalFooter
                    actions={[
                        {
                            text: t('shared.labels.done'),
                            variant: 'primary',
                            onClick: onSuccessClose
                        }
                    ]}
                />
            </UIModal>
        </>
    );
}

type ChangeEmailSectionFormProps = {
    loading: boolean;
    onSubmit: (data: { newEmail: string; password: string }) => void;
};

export type ChangeEmailSectionFormRef = {
    resetForm: () => void;
};

export const ChangeEmailSectionForm = forwardRef<
    ChangeEmailSectionFormRef,
    ChangeEmailSectionFormProps
>((props, ref): ReactElement => {
    const { loading, onSubmit } = props;

    const { t } = useMeTranslation();

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * Email
     */
    const emailRef = useRef<UIFormInputRef>(null);
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const checkEmail = useCallback(
        (email: string): string => {
            const error = validateEmail(email);
            if (error) {
                return t(error);
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setEmailError(checkEmail(email));
        }
    }, [email, showRealTimeErrors, checkEmail]);

    /**
     * Confirm Email
     */
    const confirmEmailRef = useRef<UIFormInputRef>(null);
    const [confirmEmail, setConfirmEmail] = useState('');
    const [confirmEmailError, setConfirmEmailError] = useState('');
    const checkConfirmEmail = useCallback(
        (confirmEmail: string): string => {
            const trimmed = confirmEmail.trim().toLowerCase();
            const emailTrimmed = email.trim().toLowerCase();
            if (trimmed !== emailTrimmed) {
                return t(
                    'components.change_email_section.confirm_email_error_mismatch'
                );
            }

            return '';
        },
        [email, t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setConfirmEmailError(checkConfirmEmail(confirmEmail));
        }
    }, [confirmEmail, showRealTimeErrors, checkConfirmEmail]);

    /**
     * Password
     */
    const passwordRef = useRef<UIFormInputRef>(null);
    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const checkPassword = useCallback(
        (password: string): string => {
            if (password.length === 0) {
                return t(
                    'components.change_email_section.password_error_required'
                );
            }

            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setPasswordError(checkPassword(password));
        }
    }, [password, showRealTimeErrors, checkPassword]);

    const haveValuesChanged = useMemo(() => {
        return email !== '' && confirmEmail !== '';
    }, [email, confirmEmail]);

    function resetForm() {
        setEmail('');
        setEmailError('');
        setConfirmEmail('');
        setConfirmEmailError('');
        setPassword('');
        setPasswordError('');
        setShowRealTimeErrors(false);
    }

    useImperativeHandle(ref, () => ({
        resetForm() {
            resetForm();
        }
    }));

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (
            !checkEmail(email) &&
            !checkConfirmEmail(confirmEmail) &&
            !checkPassword(password)
        ) {
            setShowRealTimeErrors(false);
            onSubmit?.({ newEmail: email, password });
        }
    };

    return (
        <UISection>
            <form onSubmit={handleSubmit} noValidate>
                <UISectionHeader
                    title={t('components.change_email_section.title')}
                />
                <UISectionContent>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 24
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.change_email_section.email_label'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.change_email_section.email_description'
                                    )}
                                </UIText>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1
                                }}
                            >
                                <UIFormInput
                                    ref={emailRef}
                                    name="New Email"
                                    value={email}
                                    placeholder={t(
                                        'components.change_email_section.email_placeholder'
                                    )}
                                    type="email"
                                    autoComplete="on"
                                    errorText={emailError}
                                    required
                                    maxLength={100}
                                    onChange={setEmail}
                                />
                            </div>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.change_email_section.confirm_email_label'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.change_email_section.confirm_email_description'
                                    )}
                                </UIText>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1
                                }}
                            >
                                <UIFormInput
                                    ref={confirmEmailRef}
                                    name="Confirm New Email"
                                    value={confirmEmail}
                                    placeholder={t(
                                        'components.change_email_section.confirm_email_placeholder'
                                    )}
                                    type="email"
                                    autoComplete="on"
                                    errorText={confirmEmailError}
                                    required
                                    maxLength={100}
                                    onChange={setConfirmEmail}
                                />
                            </div>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.change_email_section.password_label'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.change_email_section.password_description'
                                    )}
                                </UIText>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1
                                }}
                            >
                                <UIFormInput
                                    ref={passwordRef}
                                    name="Password"
                                    value={password}
                                    placeholder=""
                                    type="password"
                                    errorText={passwordError}
                                    required
                                    maxLength={100}
                                    onChange={setPassword}
                                />
                            </div>
                        </div>
                    </div>
                </UISectionContent>
                {haveValuesChanged && (
                    <UISectionFooter
                        actions={[
                            {
                                variant: 'primary',
                                type: 'submit',
                                loading: loading,
                                disabled: loading,
                                text: loading
                                    ? t('shared.labels.saving')
                                    : t('shared.labels.save')
                            },
                            {
                                text: t('shared.labels.cancel'),
                                disabled: loading,
                                onClick: resetForm
                            }
                        ]}
                    />
                )}
            </form>
        </UISection>
    );
});
